import { toast } from 'react-toastify';

import { getSerializedErrorMessage } from '../../../../../utils/errors';
import { finishTreatmentPlanV3Mutation, startTreatmentPlanV3Mutation } from '../../../utils';
import { applyAssessmentMethodVisualFile, applyAssessmentMethodVisualMetadata } from '../utils';
import { isForLoadedAssessmentPage, markAssessmentPageChanged } from './utils';

import type { ActionReducerMapBuilder } from '@reduxjs/toolkit';
import type { TreatmentPlanV3State } from '../../../types';
import type {
  AssessmentMutationAction,
  DeleteTreatmentPlanV3AssessmentMethodVisualPayload,
  ITreatmentPlanV3AssessmentMethodVisual,
  TreatmentPlanV3AssessmentThunks,
  UpdateTreatmentPlanV3AssessmentMethodVisualFilePayload,
  UpdateTreatmentPlanV3AssessmentMethodVisualPayload,
} from '../types';

export function addCreateTreatmentPlanV3AssessmentMethodVisualsCases(
  builder: ActionReducerMapBuilder<TreatmentPlanV3State>,
  thunks: TreatmentPlanV3AssessmentThunks,
) {
  const { createTreatmentPlanV3AssessmentMethodVisuals } = thunks;
  const flag = 'createAssessmentMethodVisualsLoading';

  builder
    .addCase(createTreatmentPlanV3AssessmentMethodVisuals.pending, (state, action) => {
      startTreatmentPlanV3Mutation(state, flag, action);
    })
    .addCase(createTreatmentPlanV3AssessmentMethodVisuals.fulfilled, (state, action) => {
      finishTreatmentPlanV3Mutation(state, flag, action);
    })
    .addCase(createTreatmentPlanV3AssessmentMethodVisuals.rejected, (state, action) => {
      if (!finishTreatmentPlanV3Mutation(state, flag, action)) return;
      if (!isForLoadedAssessmentPage(state, action)) return;
      toast.error(getSerializedErrorMessage(action.error));
      state.error = action.error;
    });
}

export function addUpdateTreatmentPlanV3AssessmentMethodVisualCases(
  builder: ActionReducerMapBuilder<TreatmentPlanV3State>,
  thunks: TreatmentPlanV3AssessmentThunks,
) {
  const { updateTreatmentPlanV3AssessmentMethodVisual } = thunks;
  const flag = 'updateAssessmentMethodVisualLoading';

  builder
    .addCase(updateTreatmentPlanV3AssessmentMethodVisual.pending, (state, action) => {
      startTreatmentPlanV3Mutation(state, flag, action);
    })
    .addCase(
      updateTreatmentPlanV3AssessmentMethodVisual.fulfilled,
      (
        state,
        action: AssessmentMutationAction<
          ITreatmentPlanV3AssessmentMethodVisual,
          UpdateTreatmentPlanV3AssessmentMethodVisualPayload
        >,
      ) => {
        if (!finishTreatmentPlanV3Mutation(state, flag, action)) return;
        if (!isForLoadedAssessmentPage(state, action)) return;
        const method = state.assessmentPage?.methods.find(
          (row) => row.id === action.meta.arg.methodId,
        );
        if (!method) return;
        markAssessmentPageChanged(state);
        applyAssessmentMethodVisualMetadata(method.visuals, action.payload);
      },
    )
    .addCase(updateTreatmentPlanV3AssessmentMethodVisual.rejected, (state, action) => {
      if (!finishTreatmentPlanV3Mutation(state, flag, action)) return;
      if (!isForLoadedAssessmentPage(state, action)) return;
      toast.error(getSerializedErrorMessage(action.error));
      state.error = action.error;
    });
}

export function addUpdateTreatmentPlanV3AssessmentMethodVisualFileCases(
  builder: ActionReducerMapBuilder<TreatmentPlanV3State>,
  thunks: TreatmentPlanV3AssessmentThunks,
) {
  const { updateTreatmentPlanV3AssessmentMethodVisualFile } = thunks;
  const flag = 'updateAssessmentMethodVisualFileLoading';

  builder
    .addCase(updateTreatmentPlanV3AssessmentMethodVisualFile.pending, (state, action) => {
      startTreatmentPlanV3Mutation(state, flag, action);
    })
    .addCase(
      updateTreatmentPlanV3AssessmentMethodVisualFile.fulfilled,
      (
        state,
        action: AssessmentMutationAction<
          ITreatmentPlanV3AssessmentMethodVisual,
          UpdateTreatmentPlanV3AssessmentMethodVisualFilePayload
        >,
      ) => {
        if (!finishTreatmentPlanV3Mutation(state, flag, action)) return;
        if (!isForLoadedAssessmentPage(state, action)) return;
        const method = state.assessmentPage?.methods.find(
          (row) => row.id === action.meta.arg.methodId,
        );
        if (!method) return;
        markAssessmentPageChanged(state);
        applyAssessmentMethodVisualFile(method.visuals, action.payload);
      },
    )
    .addCase(updateTreatmentPlanV3AssessmentMethodVisualFile.rejected, (state, action) => {
      if (!finishTreatmentPlanV3Mutation(state, flag, action)) return;
      if (!isForLoadedAssessmentPage(state, action)) return;
      toast.error(getSerializedErrorMessage(action.error));
      state.error = action.error;
    });
}

export function addDeleteTreatmentPlanV3AssessmentMethodVisualCases(
  builder: ActionReducerMapBuilder<TreatmentPlanV3State>,
  thunks: TreatmentPlanV3AssessmentThunks,
) {
  const { deleteTreatmentPlanV3AssessmentMethodVisual } = thunks;
  const flag = 'deleteAssessmentMethodVisualLoading';

  builder
    .addCase(deleteTreatmentPlanV3AssessmentMethodVisual.pending, (state, action) => {
      startTreatmentPlanV3Mutation(state, flag, action);
    })
    .addCase(
      deleteTreatmentPlanV3AssessmentMethodVisual.fulfilled,
      (
        state,
        action: AssessmentMutationAction<void, DeleteTreatmentPlanV3AssessmentMethodVisualPayload>,
      ) => {
        if (!finishTreatmentPlanV3Mutation(state, flag, action)) return;
        if (!isForLoadedAssessmentPage(state, action)) return;
        const method = state.assessmentPage?.methods.find(
          (row) => row.id === action.meta.arg.methodId,
        );
        if (!method) return;
        markAssessmentPageChanged(state);
        method.visuals = method.visuals.filter((row) => row.id !== action.meta.arg.visualId);
      },
    )
    .addCase(deleteTreatmentPlanV3AssessmentMethodVisual.rejected, (state, action) => {
      if (!finishTreatmentPlanV3Mutation(state, flag, action)) return;
      if (!isForLoadedAssessmentPage(state, action)) return;
      toast.error(getSerializedErrorMessage(action.error));
      state.error = action.error;
    });
}
